import { motion } from "framer-motion";
import { cta } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Long-form editorial guide — dense keyword coverage for SEO/AEO/GEO
 * and enough original textual depth to satisfy AdSense content requirements.
 */
export const Guide = () => {
  return (
    <section
      id="guide"
      className="relative py-24 md:py-36 bg-[#F7F5F0]"
      data-testid="guide-section"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <motion.div
          variants={fade}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
        >
          <div className="postmark text-[#2B3A55] mb-6">
            <span className="w-1 h-1 rounded-full bg-[#2B3A55]" />
            The Field Guide
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1C1C1A] max-w-3xl">
            How to run a{" "}
            <span className="italic text-[#D93829]">postcard marketing</span>{" "}
            campaign in the United States — a plain-English guide.
          </h2>
          <p className="mt-6 text-[#68655E] text-lg font-sans-body max-w-2xl">
            A step-by-step walkthrough of bulk postcard printing, targeted
            mailing lists, EDDM, and campaign automation — written for
            marketers who&rsquo;d rather send a card today than read a
            250-page textbook.
          </p>
        </motion.div>

        <div className="mt-16 space-y-14 text-[#3B3A36] text-lg leading-relaxed font-sans-body">
          {/* Step 1 */}
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#D93829] mb-3">
              Step 01 — The list
            </div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1A] mb-4">
              Start with a good mailing list, or make one.
            </h3>
            <p>
              Every profitable postcard campaign starts with the mailing list
              service. There are three ways to get one: upload your existing
              customer file, buy a targeted mailing list filtered by
              geography and demographics, or use an EDDM postcard service
              that delivers to every mailbox on a chosen postal route. If
              you&rsquo;re running local postcard marketing for a restaurant,
              retail store, or service business, EDDM is usually the
              cheapest per-piece way to blanket a neighborhood. If
              you&rsquo;re doing postcard marketing for realtors or
              insurance agents, you want targeted postcard marketing that
              filters by homeowner status, income band, and household
              tenure. Whichever route you take, insist on address list
              processing (CASS certification, National Change of Address, and
              deduplication) so every card you print actually reaches a live,
              deliverable U.S. address.
            </p>
          </motion.div>

          {/* Step 2 */}
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#D93829] mb-3">
              Step 02 — The card
            </div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1A] mb-4">
              Design a postcard people can&rsquo;t ignore.
            </h3>
            <p>
              Custom postcard printing today supports 4×6, 5×7, and 6×11
              formats in glossy or matte finish. The 4×6 is the workhorse
              because it qualifies for the cheapest USPS First-Class rate;
              the 6×11 dominates the mailbox and works best for real estate
              farm mailings, grand-opening announcements, and high-ticket
              service businesses. Whatever the size, four rules apply.
              First, treat the front like a billboard — one image, one
              headline, one offer. Second, add a handwritten note on the
              back; postcard design and print services now include
              robotic-handwriting engines that produce real ballpoint ink on
              cardstock, and it lifts response dramatically. Third, include
              a single call-to-action with a trackable path (QR code,
              vanity URL, or unique promo code) so you can measure ROI.
              Fourth, respect the postal safe zone: leave 5/8 of an inch
              blank on the address side or your postcard promotions will
              come back stamped &ldquo;undeliverable.&rdquo;
            </p>
          </motion.div>

          {/* Step 3 */}
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#D93829] mb-3">
              Step 03 — The send
            </div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1A] mb-4">
              Print, stamp, and mail — same day if you can.
            </h3>
            <p>
              A modern postcard printing and mailing service handles bulk
              postcard printing, mass postcard mailing, postage, and USPS
              handoff as a single workflow. Cards printed and stamped by
              Thursday afternoon typically arrive in U.S. mailboxes the
              following Monday or Tuesday — the window when consumer mail
              volume is lowest and open rates are highest. Affordable
              postcard printing usually starts around $0.90 per piece with
              postage included; low cost postcard mailing at scale can drop
              closer to $0.55. If you need faster print-and-ship postcards
              for a time-sensitive event or promotion, ask for expedited
              48-hour production — most postcard fulfillment services will
              do it at no premium.
            </p>
          </motion.div>

          {/* Step 4 */}
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#D93829] mb-3">
              Step 04 — The follow-through
            </div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1A] mb-4">
              Track, analyze, and automate what worked.
            </h3>
            <p>
              A good postcard tracking service tells you when each piece
              enters the mailstream, when it&rsquo;s expected to hit the
              mailbox, and — via QR scans or dedicated phone numbers — who
              actually responded. Postcard analytics tools compare campaigns
              side by side so you can see whether your grand-opening
              variant beat your evergreen offer, or whether Tuesday drops
              outperform Fridays. Once you know what works, postcard
              workflow automation lets you trigger cards from your CRM,
              e-commerce platform, or Zapier — new lead in HubSpot, closed
              deal in Salesforce, abandoned cart in Shopify, birthday in
              Mailchimp, renewal ninety days out. That&rsquo;s postcard
              campaign management at its best: the physical channel that
              runs like an email drip, except every touch is a real card in
              a real hand.
            </p>
          </motion.div>

          {/* Who this is for */}
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="pt-6 border-t border-[#E4DFD4]"
          >
            <h3 className="font-serif-display text-3xl text-[#1C1C1A] mb-4 mt-6">
              Who this playbook is for
            </h3>
            <p>
              Postcard marketing for small business owners who need reliable
              lead generation without another software subscription. Postcard
              marketing for realtors who work a specific ZIP code and want
              their name on the fridge when the &ldquo;we should sell&rdquo;
              conversation finally happens. Postcard marketing for insurance
              agents renewing policies or cross-selling household coverage.
              Postcard marketing for restaurants launching a menu,
              re-engaging lapsed regulars, or announcing a second location.
              Postcard marketing for retail stores driving foot traffic
              during a seasonal push. Postcard marketing for service
              businesses (plumbers, electricians, landscapers, cleaners)
              staying top-of-mind in a specific service radius. Postcard
              marketing for startups launching in a physical market.
              Postcard marketing for nonprofits thanking donors and
              recruiting new ones. Postcard marketing for events driving
              RSVPs where email invitations get lost. If any of that
              sounds like you, physical mail marketing is the highest-signal
              channel you probably aren&rsquo;t using yet.
            </p>

            <div className="pt-8">
              <a
                href={cta("guide")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="guide-cta"
                className="group inline-flex items-center gap-3 bg-[#D93829] text-white px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#1C1C1A] transition-colors duration-500"
              >
                Start your first postcard campaign
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Guide;
